import {
  DestinationHealthEnvelope,
  RuntimeAlarm,
  RuntimeSnapshot,
  Transcoder,
} from '../services/streamsService';

export const getOutputAlarmKeys = (outputId: number) => [
  `output-${outputId}`,
  `transcoder-${outputId}`,
  `pull-viewer-${outputId}`,
  `hls-session-${outputId}`,
];

export const getOutputAlarmKey = (output: Transcoder) => {
  if (output.protocol === 'hls') return `hls-session-${output.id}`;
  if (output.deliveryMode === 'PULL' || output.isPullLink === true) return `pull-viewer-${output.id}`;
  return `output-${output.id}`;
};

const outputHealthFromSnapshot = (snapshot: RuntimeSnapshot, outputId: number) =>
  snapshot.outputHealthStatuses?.[outputId]
  || snapshot.pullStatuses?.[outputId]?.destinationHealth
  || snapshot.hlsStatuses?.[outputId]?.destinationHealth
  || null;

const normalizedHealthStatus = (output: Transcoder, health?: DestinationHealthEnvelope | null) => {
  if (!output.isActive) return 'disabled';
  return String(health?.status || 'disconnected').trim().toLowerCase();
};

export const reconcileOutputHealthAlarm = (
  alarms: Record<string, RuntimeAlarm>,
  output: Transcoder,
  health?: DestinationHealthEnvelope | null,
  observedAt = new Date().toISOString(),
) => {
  const next = { ...alarms };
  const alarmKey = getOutputAlarmKey(output);
  const status = normalizedHealthStatus(output, health);
  const shouldAlarm = output.isActive === true && status === 'disconnected';

  getOutputAlarmKeys(output.id).forEach((key) => {
    if (!shouldAlarm || key !== alarmKey) delete next[key];
  });

  if (shouldAlarm && !next[alarmKey]) {
    next[alarmKey] = {
      channelId: output.channelId,
      outputId: output.id,
      alarmKey,
      raised: true,
      entityType: 'output',
      emittedAt: health?.metrics?.updatedAt || observedAt,
    };
  }

  return next;
};

export const reconcileAlarmsWithSnapshot = (
  alarms: Record<string, RuntimeAlarm>,
  snapshot: RuntimeSnapshot,
) => {
  let next = Object.fromEntries(Object.entries(alarms).filter(([alarmKey, alarm]) => {
    if (alarmKey === 'stream-offline') return !snapshot.stats?.type;
    if (alarmKey === 'bitrate-warning') return snapshot.stats?.bitrateEnforcement?.warningActive === true;
    if (alarmKey === 'bitrate-shutdown') return snapshot.stats?.bitrateEnforcement?.kickNoticeActive === true;
    if (!alarm.outputId) return true;

    const output = snapshot.transcoders?.find((entry) => entry.id === alarm.outputId);
    if (!output || output.isActive !== true) return false;
    const status = normalizedHealthStatus(output, outputHealthFromSnapshot(snapshot, output.id));
    return status === 'disconnected';
  }));

  for (const output of snapshot.transcoders || []) {
    next = reconcileOutputHealthAlarm(
      next,
      output,
      outputHealthFromSnapshot(snapshot, output.id),
      snapshot.serverTime || new Date().toISOString(),
    );
  }

  return next;
};
